/**
 * Small dialog launched from the PDF viewer header to set the project address
 * everywhere (TimberProject + EnhancedDeal + Lead) at once.
 *
 * Reuses the same `AddressAutocomplete` the rest of the CRM uses, with manual
 * structured fields underneath for last-mile corrections (autocomplete fills
 * them but the user can override).
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, MapPin, X } from 'lucide-react';
import AddressAutocomplete from '../map/AddressAutocomplete';
import { setProjectAddressEverywhere, formatLocation } from '../../utils/projectAddressService';
import type { Address, Coordinates } from '../../types';
import './SetProjectAddressModal.css';

export interface SetProjectAddressModalProps {
  dealId: string;
  initialAddress?: Address;
  initialLocation?: string;
  initialCoordinates?: Coordinates;
  onClose: () => void;
  onSaved?: (next: { address: Address; location: string; coordinates?: Coordinates; geocoded: boolean }) => void;
}

const EMPTY_ADDRESS: Address = { street: '', city: '', state: '', zipCode: '', country: 'United States' };

export default function SetProjectAddressModal({
  dealId,
  initialAddress,
  initialLocation,
  initialCoordinates,
  onClose,
  onSaved,
}: SetProjectAddressModalProps) {
  const [address, setAddress] = useState<Address>(initialAddress || EMPTY_ADDRESS);
  const [location, setLocation] = useState<string>(initialLocation || (initialAddress ? formatLocation(initialAddress) : ''));
  const [coordinates, setCoordinates] = useState<Coordinates | undefined>(initialCoordinates);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Keep `location` in sync with structured edits (only when location matches the previous formatted version).
  useEffect(() => {
    setLocation(prev => {
      if (!prev || prev === formatLocation(initialAddress || EMPTY_ADDRESS)) {
        return formatLocation(address);
      }
      return prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.street, address.city, address.state, address.zipCode]);

  const handleAutocompleteChange = (loc: string, addr?: Address, coords?: Coordinates) => {
    setLocation(loc);
    if (addr) setAddress(prev => ({ ...prev, ...addr }));
    if (coords) setCoordinates(coords);
  };

  const handleFieldChange = (field: keyof Address, value: string) => {
    setAddress(prev => ({ ...prev, [field]: value }));
    // Clear coords if the user manually edits — geocoding will rerun on save.
    setCoordinates(undefined);
  };

  const canSave = !!(address.street || address.city) && !!location.trim();

  const handleSave = async () => {
    setError(null);
    setNote(null);
    if (!canSave) {
      setError('Enter at least a street or city.');
      return;
    }
    setSaving(true);
    try {
      const finalLocation = location.trim() || formatLocation(address);
      const result = await setProjectAddressEverywhere(dealId, {
        address,
        location: finalLocation,
        coordinates,
      });
      if (!result.geocoded && !coordinates) {
        setNote("Saved — couldn't auto-locate on map; you can fix coordinates from the project page.");
      }
      setSuccess(true);
      onSaved?.({ address, location: finalLocation, coordinates, geocoded: result.geocoded });
      setTimeout(onClose, 700);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <div className="spa-overlay" onClick={onClose}>
      <div className="spa-modal" onClick={e => e.stopPropagation()}>
        <header className="spa-header">
          <h3 className="spa-title">
            <MapPin size={16} />
            <span>Project address</span>
          </h3>
          <button type="button" className="spa-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="spa-body">
          <p className="spa-help">
            Updating here writes to the project, the deal, and the originating lead so the address is consistent everywhere.
          </p>

          <div className="spa-section-label">Search</div>
          <AddressAutocomplete
            value={location}
            onChange={handleAutocompleteChange}
            placeholder="Type an address…"
          />

          <div className="spa-section-label">Address</div>
          <div className="spa-fields-grid">
            <div className="spa-field spa-field--full">
              <label htmlFor="spa-street">Street</label>
              <input
                id="spa-street"
                type="text"
                value={address.street}
                onChange={e => handleFieldChange('street', e.target.value)}
                placeholder="123 Main St"
              />
            </div>
            <div className="spa-field">
              <label htmlFor="spa-city">City</label>
              <input
                id="spa-city"
                type="text"
                value={address.city}
                onChange={e => handleFieldChange('city', e.target.value)}
              />
            </div>
            <div className="spa-field">
              <label htmlFor="spa-state">State</label>
              <input
                id="spa-state"
                type="text"
                value={address.state}
                onChange={e => handleFieldChange('state', e.target.value)}
              />
            </div>
            <div className="spa-field">
              <label htmlFor="spa-zip">Zip</label>
              <input
                id="spa-zip"
                type="text"
                value={address.zipCode}
                onChange={e => handleFieldChange('zipCode', e.target.value)}
              />
            </div>
            <div className="spa-field">
              <label htmlFor="spa-country">Country</label>
              <input
                id="spa-country"
                type="text"
                value={address.country}
                onChange={e => handleFieldChange('country', e.target.value)}
              />
            </div>
          </div>

          {error && <div className="spa-error">{error}</div>}
          {note && <div className="spa-note">{note}</div>}
        </div>

        <footer className="spa-footer">
          <button type="button" className="spa-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="spa-btn spa-btn--primary"
            onClick={handleSave}
            disabled={saving || !canSave}
          >
            {success ? <Check size={14} /> : null}
            <span>{saving ? 'Saving…' : success ? 'Saved' : 'Save'}</span>
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(body, document.body);
}
